import { colors } from "./colors"

type NumberType = 'delayInMinutes' | 'cancellationPercentage' | 'transferTimeInMinutes' | 'missedTransfersPercentage' | 'punctualityPercentage' | 'chuuchuuScore'

// Helper to convert hex to RGB
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    : [0, 0, 0];
};

// Helper to convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
};

// Helper to interpolate between two colors
const interpolateColor = (color1: string, color2: string, factor: number): string => {
  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);
  
  const r = r1 + (r2 - r1) * factor;
  const g = g1 + (g2 - g1) * factor;
  const b = b1 + (b2 - b1) * factor;
  
  return rgbToHex(r, g, b);
};

// Helper to get reliability color with gradient
// 100 = green, 80 = yellow, 50 = orange, 40 = red, below 40 = red
export const getReliabilityColorGradient = (score: number | null): string => {
  if (score == null) return colors.grey600;
  
  const roundedScore = Math.round(score);
  
  // Below 40: always red
  if (roundedScore < 40) {
    return colors.red;
  }
  
  // 100 or above: always green
  if (roundedScore >= 100) {
    return colors.green;
  }
  
  // Define yellow color (between orange and green)
  const yellow = '#EAB308'; // Tailwind yellow-500
  
  // Between 40 and 50: interpolate from red to orange
  if (roundedScore < 50) {
    const factor = (roundedScore - 40) / (50 - 40);
    return interpolateColor(colors.red, colors.orange, factor);
  }
  
  // Between 50 and 80: interpolate from orange to yellow
  if (roundedScore < 80) {
    const factor = (roundedScore - 50) / (80 - 50);
    return interpolateColor(colors.orange, yellow, factor);
  }
  
  // Between 80 and 100: interpolate from yellow to green
  const factor = (roundedScore - 80) / (100 - 80);
  return interpolateColor(yellow, colors.green, factor);
};

export const getColor = (value: number, type: NumberType): string => {
  value = Math.round(value);
  
  if (type === 'delayInMinutes') {
    if (value < 0) return colors.black
    else if (value <= 4) return colors.green
    else if (value <= 14) return colors.orange
    else return colors.red
  }

  if (type === 'cancellationPercentage') {
    if (value <= 9) return colors.green
    else if (value <= 20) return colors.orange
    else return colors.red
  }

  if (type === 'transferTimeInMinutes') {
    if (value >= 10) return colors.green
    else if (value >= 5) return colors.orange
    else return colors.red
  }

  if (type === 'missedTransfersPercentage') {
    if (value <= 9) return colors.green
    else if (value <= 20) return colors.orange
    else return colors.red
  }

  if (type === 'punctualityPercentage') {
    if (value > 80) return colors.green
    else if (value >= 60) return colors.orange
    else return colors.red
  }

  if (type === 'chuuchuuScore') {
    if (value === -1) return colors.grey600;
    // Use gradient for chuuchuu score
    return getReliabilityColorGradient(value);
  }

  return colors.grey800;
}
